'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import Image from 'next/image';
import classes from './NeumoLogoCarousel.module.scss';
import { createPortal } from 'react-dom';

type LogoItem = {
  img: string;
  href?: string;
  alt?: string; // can be the website name
  name?: string; // preferred display name
};

type NeumoLogoCarouselProps = {
  title?: string;
  logos: LogoItem[] | string[];
  speed?: number;
};

export const NeumoLogoCarousel = ({ data }: { data: NeumoLogoCarouselProps }) => {
  const { title, logos, speed = 30 } = data;

  const normalizedLogos: LogoItem[] = useMemo(() => {
    return logos.map((logo, index) => {
      if (typeof logo === 'string') {
        return { img: logo, name: `Website ${index + 1}`, alt: `Website ${index + 1}` };
      }
      return {
        ...logo,
        name: logo.name || logo.alt || `Website ${index + 1}`,
        alt: logo.alt || logo.name || `Website ${index + 1}`,
      };
    });
  }, [logos]);

  const duplicatedLogos = useMemo(
    () => [...normalizedLogos, ...normalizedLogos],
    [normalizedLogos]
  );

  // Modal state
  const [isOpen, setIsOpen] = useState(false);
  const [pending, setPending] = useState<{ href: string; name: string } | null>(null);
  const [canPortal, setCanPortal] = useState(false);

  const confirmBtnRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    // Ensures portal only runs client-side
    setCanPortal(true);
  }, []);

  const openConfirm = (href: string, name: string) => {
    setPending({ href, name });
    setIsOpen(true);
  };

  const closeConfirm = () => {
    setIsOpen(false);
    setPending(null);
  };

  const onConfirm = () => {
    if (!pending) {
      return;
    }
    window.open(pending.href, '_blank', 'noopener,noreferrer');
    closeConfirm();
  };

  // Accessibility: ESC to close, focus confirm button on open, lock scroll
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeConfirm();
      }
    };

    window.addEventListener('keydown', onKeyDown);

    // lock scroll while modal open
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    setTimeout(() => confirmBtnRef.current?.focus(), 0);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  const modal =
    isOpen && pending ? (
      // Keyboard dismissal is handled globally via the Escape key listener above.
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
      <div
        className={classes.modalOverlay}
        role="dialog"
        aria-modal="true"
        aria-labelledby="open-website-title"
        onClick={closeConfirm}
      >
        {/* Backdrop click closes via the overlay; this only stops propagation. */}
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
        <div className={classes.modal} onClick={(e) => e.stopPropagation()}>
          <h3 id="open-website-title" className={classes.modalTitle}>
            Open “{pending.name}”?
          </h3>
          <p className={classes.modalText}>This will open the website in a new tab.</p>

          <div className={classes.modalActions}>
            <button
              type="button"
              className={`${classes.btn} ${classes.btnCancel}`}
              onClick={closeConfirm}
            >
              Cancel
            </button>
            <button
              type="button"
              ref={confirmBtnRef}
              className={`${classes.btn} ${classes.btnConfirm}`}
              onClick={onConfirm}
            >
              Open in new tab
            </button>
          </div>
        </div>
      </div>
    ) : null;

  return (
    <section className={classes.section} aria-label={title || 'Websites carousel'}>
      {title && (
        <div className={classes.header}>
          <h2 className={classes.title}>{title}</h2>
        </div>
      )}

      <div className={classes.carousel}>
        <div className={classes.fadeLeft} aria-hidden="true" />
        <div className={classes.fadeRight} aria-hidden="true" />

        <div className={classes.track} style={{ animationDuration: `${speed}s` }}>
          {duplicatedLogos.map((logo, index) => {
            const name = logo.name || logo.alt || `Website ${index + 1}`;
            const isClickable = Boolean(logo.href);

            return (
              <div key={`${logo.img}-${index}`} className={classes.logoSlide}>
                {isClickable ? (
                  <button
                    type="button"
                    className={classes.logoButton}
                    onClick={() => openConfirm(logo.href!, name)}
                    aria-label={`Open ${name} in a new tab`}
                  >
                    <Image
                      src={logo.img}
                      alt={logo.alt || name}
                      width={800}
                      height={500}
                      className={classes.logoImage}
                      loading="lazy"
                    />
                    <div className={classes.overlay} aria-hidden="true">
                      <div className={classes.overlayLabel}>{name}</div>
                    </div>
                  </button>
                ) : (
                  <>
                    <Image
                      src={logo.img}
                      alt={logo.alt || name}
                      width={800}
                      height={500}
                      className={classes.logoImage}
                      loading="lazy"
                    />
                    <div className={classes.overlay} aria-hidden="true">
                      <div className={classes.overlayLabel}>{name}</div>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ✅ Portal ensures modal is ABOVE everything */}
      {canPortal && modal ? createPortal(modal, document.body) : null}
    </section>
  );
};
